import { useDraggable } from '@dnd-kit/core';
import { addHours, format } from 'date-fns';
import DisciplineActivityCard, {
  DisciplineMoveContentParams,
} from '../DisciplineActivityCard';
import { ScheduleDayContent } from '../../../model/ScheduleHub';
import { useDailyScheduleContentStore } from '../../../stores/useDailyScheduleContent';
import { isScheduleHubCardClickBlocked } from '../clickGuards';

export type ScheduleActivityClickPayload = ScheduleDayContent & {
  day: string;
  week: number;
};

interface IDraggableDisciplineCard {
  id: string;
  dayKey: string;
  activityIndex: number;
  activity: ScheduleDayContent;
  clickPayload?: ScheduleActivityClickPayload;
  onMoveContentSuccess?: () => void;
  onMoveContentConfirm?: (
    params: DisciplineMoveContentParams
  ) => Promise<void> | void;
  onCardClick?: (payload: ScheduleActivityClickPayload) => void;
  hasEssay?: boolean;
}

const DraggableDisciplineCard = ({
  id,
  dayKey,
  activityIndex,
  activity,
  clickPayload,
  onMoveContentSuccess,
  onMoveContentConfirm,
  onCardClick,
  hasEssay,
}: IDraggableDisciplineCard) => {
  const { setContentListFilter } = useDailyScheduleContentStore((s) => ({
    setContentListFilter: s.setContentListFilter,
  }));
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data: { sourceDayKey: dayKey, activityIndex, activity },
  });

  const handleClick = () => {
    if (isScheduleHubCardClickBlocked()) {
      return;
    }

    setContentListFilter('all');
    if (clickPayload) {
      onCardClick?.(clickPayload);
    } else {
      onCardClick?.({ ...activity, day: dayKey, week: 0 });
    }
  };

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={handleClick}
      className={`cursor-grab active:cursor-grabbing ${isDragging ? 'invisible' : ''}`}
    >
      <DisciplineActivityCard
        compact
        activityIndex={activityIndex}
        discipline={activity.discipline}
        progress={activity.progress}
        isRevision={activity.isRevision}
        isCompleted={activity.isCompleted}
        originalDay={activity.originalData ? format(addHours(new Date(activity.originalData), 3), 'yyyy-MM-dd') : undefined}
        dayKey={dayKey}
        onMoveContentSuccess={onMoveContentSuccess}
        onMoveContentConfirm={onMoveContentConfirm}
        hasEssay={hasEssay}
      />
    </div>
  );
};

export default DraggableDisciplineCard;
